import { useState } from 'react';
import { motion } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Send, Loader2, Mail, Phone, MapPin, Facebook, ArrowUpRight } from 'lucide-react';
import { api } from '@/lib/api';
import { KRAFT } from '@/constants/testIds';
import { FACEBOOK_GROUP_URL } from '@/components/Gallery';

export default function ContactForm() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = form.name.trim() && form.email.trim() && form.message.trim().length >= 5;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      await api.post('/contact', {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        subject: form.subject.trim() || null,
        message: form.message.trim(),
      });
      toast.success('Wiadomość wysłana', {
        description: 'Odpowiemy w ciągu 24 godzin roboczych.',
      });
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Nie udało się wysłać wiadomości. Spróbuj ponownie.';
      toast.error('Błąd wysyłki', { description: msg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid={KRAFT.contactRoot}
      className="relative bg-ink-950 py-24 md:py-32"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-white/10">
          {/* Left copy + contact channels */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 bg-ink-950 p-8 md:p-12 flex flex-col justify-between"
          >
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-px bg-volt" />
                <span className="eyebrow">Napisz do nas</span>
              </div>
              <h2 className="font-display uppercase text-white text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
                Zapytanie <br />
                <span className="text-volt italic font-normal">bez zobowiązań.</span>
              </h2>
              <p className="mt-6 text-slate-300 leading-relaxed max-w-md">
                Wycena, konsultacja techniczna, weryfikacja projektu — odpowiadamy w ciągu 24 godzin
                roboczych. Poza godzinami — dyżur telefoniczny 24/7.
              </p>
            </div>

            <div className="mt-10 space-y-4">
              <a
                href="tel:+48731842736"
                className="group flex items-center gap-4 p-4 border border-white/10 hover:border-volt/60 transition-colors"
              >
                <div className="w-10 h-10 border border-volt/50 flex items-center justify-center group-hover:bg-volt transition-colors">
                  <Phone className="w-4 h-4 text-volt group-hover:text-ink-950 transition-colors" />
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">Telefon główny</div>
                  <div className="font-display text-white text-lg tracking-tight">+48 731 842 736</div>
                </div>
              </a>
              <a
                href="tel:+48886538014"
                className="group flex items-center gap-4 p-4 border border-white/10 hover:border-volt/60 transition-colors"
              >
                <div className="w-10 h-10 border border-volt/50 flex items-center justify-center group-hover:bg-volt transition-colors">
                  <Phone className="w-4 h-4 text-volt group-hover:text-ink-950 transition-colors" />
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">Telefon dodatkowy</div>
                  <div className="font-display text-white text-lg tracking-tight">+48 886 538 014</div>
                </div>
              </a>
              <div className="flex items-center gap-4 p-4 border border-white/10">
                <div className="w-10 h-10 border border-volt/50 flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-volt" />
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">Biuro</div>
                  <div className="font-display text-white text-base tracking-tight">Wał Miedzeszyński 466U, 03-994 Warszawa</div>
                </div>
              </div>
              <a
                data-testid={KRAFT.contactFacebookLink}
                href={FACEBOOK_GROUP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-4 p-4 border border-white/10 hover:border-volt/60 transition-colors"
              >
                <div className="w-10 h-10 border border-volt/50 flex items-center justify-center group-hover:bg-volt transition-colors">
                  <Facebook className="w-4 h-4 text-volt group-hover:text-ink-950 transition-colors" />
                </div>
                <div className="flex-1">
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">Grupa branżowa · Facebook</div>
                  <div className="font-display text-white text-base tracking-tight">Portfolio realizacji · społeczność klientów</div>
                </div>
                <ArrowUpRight className="w-4 h-4 text-slate-400 group-hover:text-volt transition-colors" />
              </a>
            </div>
          </motion.div>

          {/* Right form */}
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-7 bg-ink-900 p-8 md:p-12 space-y-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">Imię i nazwisko *</Label>
                <Input
                  data-testid={KRAFT.contactInputName}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  className="mt-2 bg-ink-950 border-white/10 rounded-none focus-visible:ring-0 focus-visible:border-volt text-white h-12"
                  placeholder="np. Anna Nowak"
                />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">Telefon</Label>
                <Input
                  data-testid={KRAFT.contactInputPhone}
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  type="tel"
                  className="mt-2 bg-ink-950 border-white/10 rounded-none focus-visible:ring-0 focus-visible:border-volt text-white h-12"
                  placeholder="+48 ..."
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">E-mail *</Label>
                <Input
                  data-testid={KRAFT.contactInputEmail}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  type="email"
                  className="mt-2 bg-ink-950 border-white/10 rounded-none focus-visible:ring-0 focus-visible:border-volt text-white h-12"
                  placeholder="ty@firma.pl"
                />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">Temat</Label>
                <Input
                  data-testid={KRAFT.contactInputSubject}
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  className="mt-2 bg-ink-950 border-white/10 rounded-none focus-visible:ring-0 focus-visible:border-volt text-white h-12"
                  placeholder="np. Odbiór instalacji do polisy"
                />
              </div>
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">Wiadomość *</Label>
              <Textarea
                data-testid={KRAFT.contactInputMessage}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                required
                rows={6}
                className="mt-2 bg-ink-950 border-white/10 rounded-none focus-visible:ring-0 focus-visible:border-volt text-white resize-none"
                placeholder="Opisz zakres prac, adres wykonania i preferowany termin..."
              />
              <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                Minimalna długość: 5 znaków
              </div>
            </div>

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pt-4 border-t border-white/10">
              <div className="flex items-center gap-3 text-slate-400 text-sm">
                <Mail className="w-4 h-4 text-volt" />
                <span>Odpowiedź w ciągu 24h roboczych</span>
              </div>
              <button
                data-testid={KRAFT.contactSubmit}
                type="submit"
                disabled={!canSubmit || submitting}
                className="btn-volt disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Wysyłanie...
                  </>
                ) : (
                  <>
                    Wyślij wiadomość
                    <Send className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
